import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { Company } from 'src/app/domains/companies/models';
import { EquipmentContent } from 'src/app/domains/equipment/models';
import { EquipmentService } from 'src/app/domains/equipment/services';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { AllocateToPbxPopupComponent } from '../allocate-to-pbx-popup/allocate-to-pbx-popup.component';
import { AssignToUserPopupComponent } from '../assign-to-user-popup/assign-to-user-popup.component';
import { ChangeOutboundDidPopupComponent } from '../change-outbound-did-popup/change-outbound-did-popup.component';

@Component({
  selector: 'gdp-single-equipment-table',
  templateUrl: './single-equipment-table.component.html',
  styleUrls: ['./single-equipment-table.component.scss']
})
export class SingleEquipmentTableComponent implements OnInit {
  @Input() equipment: EquipmentContent;
  @Input() company: Company;
  @Output() changedPbx: EventEmitter<boolean> = new EventEmitter<boolean>();
  public loading = {
    loadingDelete: false,
    loadingAllocate: false,
    loadingAssign: false,
    loadingBlacklist: false,
    loadingBlf: false,
    loadingSendInstructions: false,
    loadingReset: false,
    loadingRegistered: false,
    loadingReboot: false,
    loadingOutboundDid: false,
    loadingFwUPdateflag: false
  }
  public isEquipmentRegistered: boolean;
  private loadingAllocateSubscription: Subscription;
  private loadingAssignSubscription: Subscription;

  constructor(
    private modalServce: NgbModal,
    private notification: NotificationService,
    private equipmentService: EquipmentService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.getRegisteredStatus();
  }

  deleteEquipment() {
    this.notification.showConfirmation('equipment.details.confirm-delete').then((result: string) => {
      if (result === 'ok') {
        this.loading.loadingDelete = true;
        this.equipmentService.deleteEquipment(this.equipment.mac_address)
          .subscribe(
            (res: any) => {
              this.router.navigate(['../'], { relativeTo: this.route });
              this.notification.success(
                this.translate.instant(
                  'equipment.details.delete-success',
                  { value: this.equipment.mac_address }
                )
              );
              this.loading.loadingDelete = false;
            }, () => {
              this.loading.loadingDelete = false;
            }
          );
      }
    });
  }

  openAllocateToPbxPopup() {
    const modal = this.modalServce.open(AllocateToPbxPopupComponent, {
      size: 'md'
    });
    modal.componentInstance.equipment = this.equipment;
    this.loadingAllocateSubscription = modal.componentInstance
      .onLoadingAllocate
      .subscribe((res: any) => {
        this.loading.loadingAllocate = res;
      });
    modal.componentInstance.onAllocateToPbx.pipe(take(1)).subscribe((res: any) => {
      this.equipment.pbx = res.pbx;
      this.equipment.status = res.status;
      this.equipment.outbound_did = res.outbound_did;
      this.changedPbx.emit(true);
      this.loading.loadingAllocate = false;
    });
    this.changedPbx.emit(false);
  }

  deallocateFromPbx() {
    this.notification.showConfirmation('equipment.details.confirm-deallocate').then((result: string) => {
      if (result === 'ok') {
        this.loading.loadingAllocate = true;
        this.equipmentService.deallocateFromPbx(this.equipment.pbx.id, this.equipment.mac_address)
          .subscribe(
            (res: any) => {
              this.notification.success(
                this.translate.instant(
                  'equipment.details.deallocate-success',
                  { equipment: this.equipment.mac_address, pbx: this.equipment.pbx.id }
                )
              );
              this.equipment.pbx = res.pbx;
              this.equipment.status = res.status;
              this.equipment.outbound_did = res.outbound_did;
              this.changedPbx.emit(true);
              this.loading.loadingAllocate = false;
            }, () => {
              this.loading.loadingAllocate = false;
            });
        this.changedPbx.emit(false);
      }
    });
  }

  toggleBlacklist() {
    this.loading.loadingBlacklist = true;
    this.equipmentService.setEquipmentBlacklist(this.equipment).subscribe(
      () => {
        if (this.equipment.blacklisted) {
          this.notification.success('equipment.details.nav-tabs.overview.table.blacklisted.notification.removed-success');
        }
        else {
          this.notification.success('equipment.details.nav-tabs.overview.table.blacklisted.notification.added-success');
        }
        this.equipment.blacklisted = !this.equipment.blacklisted;
        this.loading.loadingBlacklist = false;
      }, () => {
        this.loading.loadingBlacklist = false;
      }
    );
  }

  toggleBlf() {
    this.loading.loadingBlf = true;
    this.equipmentService.setEquipmentBlf(this.equipment).subscribe(
      response => {
        this.equipment.blf = !this.equipment.blf;
        this.loading.loadingBlf = false;
      }, () => {
        this.loading.loadingBlf = false;
      }
    );
  }

  sendInstructions(mac_address: string) {
    this.loading.loadingSendInstructions = true;
    this.equipmentService.sendInstructionsEmail(
      mac_address,
      [
        {
          status: 400,
          message: 'equipment.details.nav-tabs.overview.table.send-instructions.notification.error'
        }
      ]
    ).subscribe(
      () => {
        this.notification.success('equipment.details.nav-tabs.overview.table.send-instructions.notification.success');
        this.loading.loadingSendInstructions = false;
      }, () => {
        this.loading.loadingSendInstructions = false;
      }
    );
  }

  getSendInstructionsTooltip() {
    return this.equipment.pbx ? 'equipment.details.nav-tabs.overview.table.send-instructions.button.tooltip.success' : 'equipment.details.nav-tabs.overview.table.send-instructions.button.tooltip.not-assigned';
  }

  resetProvisioning() {
    this.loading.loadingReset = true;
    this.equipmentService
      .resetEquipmentProvisioning(this.equipment.mac_address)
      .subscribe(
        (r: EquipmentContent) => {
          this.equipment = r;
          this.notification.success('equipment.details.reset-success');
          this.loading.loadingReset = false;
        }, () => {
          this.loading.loadingReset = false;
        }
      );
  }

  openAssignUserPopup() {
    const modal = this.modalServce.open(AssignToUserPopupComponent, {
      size: 'md'
    });
    modal.componentInstance.equipment = this.equipment;
    this.loadingAssignSubscription = modal.componentInstance
      .onLoadingAssign
      .subscribe((res: any) => {
        this.loading.loadingAssign = res;
      });
    modal.componentInstance.onAssignToUser
      .pipe(take(1))
      .subscribe(
        (res: EquipmentContent) => {
          this.equipment.user = res.user;
          this.equipment.status = res.status;
          this.equipment.outbound_did = res.outbound_did;
          this.loading.loadingAssign = false;
        }, () => {
          this.loading.loadingAssign = false;
        });
  }

  unassignFromUser() {
    this.notification.showConfirmation('equipment.details.confirm-unassign').then((result: string) => {
      if (result === 'ok') {
        this.loading.loadingAssign = true;
        this.equipmentService.unassignFromUser(this.equipment.pbx.id, this.equipment.user.id, this.equipment.mac_address).subscribe((res: any) => {
          this.notification.success(
            this.translate.instant(
              'equipment.details.unassign-success',
              {
                equipment: this.equipment.mac_address,
                firstName: this.equipment.user.first_name,
                lastName: this.equipment.user.last_name,
                extension: this.equipment.user.extension.number
              })
          );
          this.equipment.user = res.user;
          this.equipment.status = res.status;
          this.equipment.outbound_did = res.outbound_did;
          this.loading.loadingAssign = false;
        }, () => {
            this.loading.loadingAssign = false;
          });
      }
    });
  }

  rebootEquipment() {
    this.notification.showConfirmation('equipment.details.confirm-reboot').then((result: string) => {
      if (result === 'ok') {
        this.loading.loadingReboot = true;
        this.equipmentService.rebootEquipmentById(this.equipment.id,
          [
            {
              status: 404,
              message: 'equipment.details.reboot-not-found',
              data: { value: this.equipment.mac_address }
            }
          ])
        .subscribe(
          (response: HttpResponse<null>) => {
            this.notification.success(
              this.translate.instant(
                'equipment.details.reboot-success',
                { value: this.equipment.mac_address }
              ));
            this.loading.loadingReboot = false;
          }, () => {
            this.loading.loadingReboot = false;
          }
        )
      }
    });
  }


  updateFwUpdateFlag() {
    this.loading.loadingFwUPdateflag = true;
    this.equipmentService.updateFirmwareUpdateFlag(this.equipment.mac_address, !this.equipment.fw_update).subscribe(
      response => {
        this.equipment.fw_update = !this.equipment.fw_update
        let tk = this.equipment.fw_update ? 'equipment.details.firmware-update-enabled' : 'equipment.details.firmware-update-disabled';
        this.notification.success(
          this.translate.instant(tk,
            {
              value: this.equipment.mac_address
            }
          )
        )
        this.loading.loadingFwUPdateflag = false;
      }, () => {
        this.loading.loadingFwUPdateflag = false;
      }
    )
  }

  getRegisteredStatus() {
    this.loading.loadingRegistered = true;
    this.equipmentService.getEquipmentRegistered(this.equipment.mac_address).subscribe(
      (registered: boolean) => {
        this.isEquipmentRegistered = registered;
        this.loading.loadingRegistered = false;
      }, () => {
        this.loading.loadingRegistered = false;
      }
    );
  }

  updateOutboundDid() {
    // open modal
    const modalRef = this.modalServce.open(ChangeOutboundDidPopupComponent);
    modalRef.componentInstance.currentDid = this.equipment.outbound_did;
    modalRef.componentInstance.pbxId = this.equipment.pbx.id;
    modalRef.result.then((result: string) => {
      if (!result) return;
      this.loading.loadingOutboundDid = true;
      this.equipmentService.updateOutboundDid(this.equipment.mac_address, result).subscribe(
        (response: EquipmentContent) => {
          const oldDid = this.equipment.outbound_did || '-';
          this.equipment.outbound_did = response.outbound_did;
          this.notification.success(
            this.translate.instant('equipment.details.change-did-success',
              {
                oldDid: oldDid,
                newDid: result
              }
            ));
          this.loading.loadingOutboundDid = false;
        }, () => {
          this.loading.loadingOutboundDid = false;
        }
      );
    }).catch(e => {
      // clicked outside the modal to close or used dismiss.
      // should do nothing here as it's not a save new did
    });
  }
  // refresh outbound did
  getRebootTooltip() {
    return this.company && this.company.readOnly || !this.isEquipmentRegistered ? 'You can not reboot a device not registered' : ''
  }

  ngOnDestroy() {
    if (this.loadingAssignSubscription) {
      this.loadingAssignSubscription.unsubscribe();
    }
    if (this.loadingAllocateSubscription) {
      this.loadingAllocateSubscription.unsubscribe();
    }
  }

}
